import React, { useState, useEffect } from "react";

import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import Section from "./Section";

const AboutMeSection: React.FC = () => {
  const [isCoFoundersExpanded, setIsCoFoundersExpanded] =
    useState<boolean>(false);

  // Set initial state based on screen size (only once on mount)
  useEffect(() => {
    const isMobile = window.innerWidth < 768; // md breakpoint
    setIsCoFoundersExpanded(!isMobile); // Expanded on desktop, collapsed on mobile
  }, []); // Empty dependency array ensures this only runs once on mount

  return (
    <Section id="about-me" height="auto" className="py-16">
      <div className="max-w-6xl mx-auto px-4 w-full">
        <div className="p-4 sm:p-6 md:bg-white/80 md:backdrop-blur-lg md:rounded-3xl md:p-8 lg:p-12 md:border md:border-pink-200 md:shadow-xl md:hover:shadow-2xl md:hover:border-purple-300 md:transition-all md:duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-center">
            {/* Profile Section - 1/3 width on large screens */}
            <div className="flex flex-col items-center text-center space-y-4 lg:col-span-1">
              <div className="relative">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-gradient-to-br from-pink-400 to-purple-500 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <img
                    src="/founders/Shruti.jpeg"
                    alt="Founder Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl md:text-3xl font-bold text-purple-800 font-display">
                  Shruti Badoni
                </h3>
                <p className="text-purple-600 font-medium text-lg">
                  Co-Founder & Content
                </p>
                <div className="flex justify-center space-x-2 mt-3">
                  <span className="px-3 py-1 bg-gradient-to-r from-pink-100 to-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    Solo Traveler
                  </span>
                </div>
              </div>
            </div>

            {/* Content Section - 2/3 width on large screens */}
            <div className="space-y-6 lg:col-span-2">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold text-purple-800 font-display">
                  Building Safer Journeys for Women
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full"></div>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  As a frequent traveler, I&apos;ve often found myself in
                  situations where commuting alone didn&apos;t feel safe. On top
                  of that, rides have become increasingly expensive — especially
                  for long trips like airport drops or intercity travel.
                </p>

                <p className="text-lg">
                  That&apos;s when I realized we needed a platform specifically
                  designed for women travelers - a space where we could connect
                  with verified fellow passengers, share rides safely, and build
                  a community of trust.
                </p>

                <p className="text-lg">
                  <span className="font-semibold text-purple-800">
                    Cohop was born from this need.
                  </span>{" "}
                  We&apos;re not just connecting travelers; we&apos;re creating
                  a verified network where safety comes first, costs are shared,
                  and every journey feels a little less lonely.
                </p>

                <p className="text-lg">
                  For our MVP, we&apos;re starting simple — building a small,
                  verified travel community and matching people through trusted
                  WhatsApp groups. It&apos;s all about trust first, scale later.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Co-founders Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsCoFoundersExpanded(!isCoFoundersExpanded)}
              className="group flex items-center justify-center mx-auto space-x-3 hover:bg-purple-50 rounded-lg px-4 py-2 transition-all duration-300"
            >
              {isCoFoundersExpanded ? (
                <FaChevronUp className="text-purple-600 text-xl group-hover:text-purple-800 transition-colors duration-300" />
              ) : (
                <FaChevronDown className="text-purple-600 text-xl group-hover:text-purple-800 transition-colors duration-300" />
              )}
              <h2 className="text-3xl md:text-4xl font-bold text-purple-800 font-display">
                Meet the Co-Founders
              </h2>
              {isCoFoundersExpanded ? (
                <FaChevronUp className="text-purple-600 text-xl group-hover:text-purple-800 transition-colors duration-300" />
              ) : (
                <FaChevronDown className="text-purple-600 text-xl group-hover:text-purple-800 transition-colors duration-300" />
              )}
            </button>
            <div className="w-20 h-1 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full mx-auto mt-4"></div>
          </div>

          {isCoFoundersExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fadeIn">
              {/* Manan Jhaveri */}
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-6 border border-pink-200 shadow-lg hover:shadow-xl hover:border-purple-300 transition-all duration-300">
                <div className="text-center mb-4">
                  <div className="relative mb-4">
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-gradient-to-br from-pink-400 to-purple-500 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <img
                        src="/founders/Manan.jpeg"
                        alt="Manan Jhaveri Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-purple-800 font-display mb-1">
                    Manan Jhaveri
                  </h3>
                  <p className="text-purple-600 font-medium">
                    Co-Founder & Technology
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  I&apos;ve always been passionate about solving real-world
                  problems with technology. When Shruti first shared the
                  challenges that solo women travellers face — especially the
                  fear and cost of commuting late at night — I knew this was
                  something worth building for. At Cohop, I look after the
                  technology, making sure our platform is secure, reliable, and
                  scalable. For me, safety isn&apos;t just about physical
                  travel, but also about how we handle data and build trust
                  digitally. That&apos;s why every line of code I write is
                  focused on keeping our users protected while connecting them
                  to safer, affordable journeys.
                </p>
              </div>

              {/* Snigdh Katiyar */}
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-6 border border-pink-200 shadow-lg hover:shadow-xl hover:border-purple-300 transition-all duration-300">
                <div className="text-center mb-4">
                  <div className="relative mb-4">
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-gradient-to-br from-pink-400 to-purple-500 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <img
                        src="/founders/Snigdh.jpeg"
                        alt="Snigdh Katiyar Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-purple-800 font-display mb-1">
                    Snigdh Katiyar
                  </h3>
                  <p className="text-purple-600 font-medium">
                    Co-Founder & Growth
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  When I heard Shruti&apos;s story, it immediately struck me how
                  common this problem is — not just for women, but for all solo
                  travellers. I knew we had to turn this into more than just an
                  idea. My role at Cohop is to make sure we&apos;re not just
                  building a product, but also building a community around it.
                  From marketing the concept to ensuring smooth operations, my
                  vision is to scale Cohop into a platform that transforms how
                  people commute — starting with safer rides for women, and one
                  day becoming the go-to solution for solo travellers
                  everywhere.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default AboutMeSection;
